import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAwsAccountService } from '../services/awsAccountService';
import { awsAccountKeys } from './queryKeys';

const message = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

/**
 * The two writes on one account's baselines: switch an optional baseline, and
 * run the account now. Each starts a run and returns, so both invalidate the
 * status read rather than waiting for its poll.
 */
export function useAccountBaselineActions(customerId: string, awsAccountId: string) {
  const service = useAwsAccountService();
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: awsAccountKeys.baselineStatus(customerId) });

  const setOptionalMutation = useMutation({
    mutationFn: ({ baseline, enabled }: { baseline: string; enabled: boolean }) =>
      service.setAccountOptionalBaseline(customerId, awsAccountId, baseline, enabled),
    onSuccess: invalidate,
  });

  const runMutation = useMutation({
    mutationFn: () => service.runAccountBaseline(customerId, awsAccountId),
    onSuccess: invalidate,
  });

  const failed = setOptionalMutation.error
    ? message(setOptionalMutation.error, 'Could not switch the baseline')
    : runMutation.error
      ? message(runMutation.error, 'Could not start the run')
      : null;

  return {
    setOptional: (baseline: string, enabled: boolean) => setOptionalMutation.mutate({ baseline, enabled }),
    runNow: () => runMutation.mutate(),
    busy: setOptionalMutation.isPending || runMutation.isPending,
    error: failed,
    clearError: () => {
      setOptionalMutation.reset();
      runMutation.reset();
    },
  };
}
